/**
 * Parses and validates the text pasted into the "bulk add" modal for list
 * settings (e.g. the Choices list of a form Select field).
 *
 * Each non-blank line is parsed as a small CSV record (an RFC 4180 subset), so
 * text and value may themselves contain commas when wrapped in double quotes:
 *   - `Text, Value`               → text + value.
 *   - `Text`                      → no value; the text is reused as the value
 *                                    (mirrors the single-add default).
 *   - `"Los Angeles, CA", la`     → a quoted field may contain commas.
 *   - `Text, "a ""quoted"" value"`→ a literal `"` inside a quoted field is `""`.
 *
 * Curly/smart double quotes (from a rich-text paste) are normalized to straight
 * quotes before parsing, so `“Los Angeles, CA”, la` works like the straight
 * version.
 *
 * Delimiters outside quotes separate the (up to two) fields; delimiters inside
 * quotes are literal. A line that resolves to more than two fields is ambiguous
 * (usually an unquoted delimiter inside the text/value) and is reported with a
 * hint to wrap that part in quotes.
 *
 * A line is tab-delimited when it contains a tab, and comma-delimited
 * otherwise, so columns copied from a spreadsheet (which arrive on the
 * clipboard tab-separated) parse as `Text<TAB>Value` without the user having to
 * reformat them.
 *
 * The per-line errors are: a missing text label (nothing before the first
 * separator), a trailing separator with no value after it (`Text,`, treated as
 * a mistake rather than an empty value), an ambiguous/over-split line, a
 * malformed quote, and a blank line before the last entry. A trailing blank
 * line is ignored, since a trailing newline is a common paste artifact.
 *
 * Validation is all-or-nothing: if any line is invalid the whole paste is
 * rejected and every offending line is reported.
 *
 * Note: this intentionally does NOT reject duplicate/colliding values. A plain
 * HTML `<select>` allows multiple options with the same value, and the
 * single-add flow applies no such validation, so bulk add stays consistent.
 */

export type BulkListItem = {
  text: string;
  value: string;
};

export type BulkListItemError = {
  /**
   * 1-based line number in the original input. `0` is used for errors that are
   * not tied to a specific line (e.g. empty input).
   */
  line: number;
  message: string;
};

export type ParseBulkListItemsResult =
  | {ok: true; items: BulkListItem[]}
  | {ok: false; errors: BulkListItemError[]};

type CsvLineResult = {ok: true; fields: string[]} | {ok: false};

/**
 * Whitespace that can be skipped between fields. The active delimiter is never
 * skippable, so a tab-delimited line keeps its tabs as separators.
 */
const isSkippableSpace = (char: string | undefined, delimiter: string) =>
  (char === ' ' || char === '\t') && char !== delimiter;

/**
 * The separator for a line. Copying columns out of a spreadsheet puts
 * tab-separated text on the clipboard, so a line containing a tab is treated as
 * tab-delimited; otherwise the documented comma format applies. A tab can't be
 * typed into a textarea (Tab moves focus), so its presence reliably indicates a
 * pasted table rather than intentional content.
 */
const delimiterFor = (line: string) => (line.includes('\t') ? '\t' : ',');

/**
 * Splits a single line into fields on unquoted delimiters (RFC 4180 subset).
 * Unquoted fields are trimmed; quoted fields preserve their inner content and
 * unescape `""` to `"`. Returns `{ok: false}` for a malformed quote (an
 * unterminated quote, or stray characters after a closing quote).
 */
const parseCsvLine = (line: string, delimiter: string): CsvLineResult => {
  const fields: string[] = [];
  const length = line.length;
  let i = 0;
  let parsing = true;

  while (parsing) {
    while (i < length && isSkippableSpace(line[i], delimiter)) {
      i++;
    }

    let field = '';

    if (line[i] === '"') {
      i++;
      let closed = false;
      while (i < length) {
        if (line[i] === '"') {
          if (line[i + 1] === '"') {
            field += '"';
            i += 2;
          } else {
            i++;
            closed = true;
            break;
          }
        } else {
          field += line[i];
          i++;
        }
      }
      if (!closed) {
        return {ok: false};
      }
      while (i < length && isSkippableSpace(line[i], delimiter)) {
        i++;
      }
      if (i < length && line[i] !== delimiter) {
        return {ok: false};
      }
    } else {
      while (i < length && line[i] !== delimiter) {
        field += line[i];
        i++;
      }
      field = field.trim();
    }

    fields.push(field);

    if (i < length && line[i] === delimiter) {
      i++;
    } else {
      parsing = false;
    }
  }

  return {ok: true, fields};
};

const QUOTE_EXAMPLE = '"Los Angeles, CA", los-angeles';

export const parseBulkListItems = (input: string): ParseBulkListItemsResult => {
  // Normalize curly/smart double quotes (U+201C/U+201D) to straight quotes, so
  // a paste from a rich-text source (Google Docs, Word) that auto-converted the
  // quotes is still recognized as CSV field wrapping.
  const lines = input.replace(/[\u201C\u201D]/g, '"').split(/\r?\n/);

  // Index of the last non-blank line, so trailing blank lines (e.g. a trailing
  // newline left over from a paste) are ignored rather than flagged as errors.
  let lastContentIndex = -1;
  lines.forEach((line, index) => {
    if (line.trim() !== '') {
      lastContentIndex = index;
    }
  });

  const errors: BulkListItemError[] = [];
  const items: BulkListItem[] = [];

  lines.forEach((rawLine, index) => {
    const lineNumber = index + 1;

    if (rawLine.trim() === '') {
      // Ignore trailing blank lines; flag a blank line that appears before the
      // last real entry (a leading or in-between gap).
      if (index < lastContentIndex) {
        errors.push({
          line: lineNumber,
          message: `Line ${lineNumber}: remove this empty line.`,
        });
      }
      return;
    }

    const delimiter = delimiterFor(rawLine);
    const separator = delimiter === '\t' ? 'tab' : 'comma';

    const parsed = parseCsvLine(rawLine, delimiter);
    if (!parsed.ok) {
      errors.push({
        line: lineNumber,
        message: `Line ${lineNumber}: check the double quotes around the text or value — for example ${QUOTE_EXAMPLE}.`,
      });
      return;
    }

    const {fields} = parsed;
    const text = fields[0] ?? '';

    if (text === '') {
      errors.push({
        line: lineNumber,
        message: `Line ${lineNumber}: add the option text before the ${separator}.`,
      });
      return;
    }

    if (fields.length > 2) {
      errors.push({
        line: lineNumber,
        message:
          delimiter === '\t'
            ? `Line ${lineNumber}: only a text and a value are supported. Remove the extra tab-separated columns, or wrap a text or value containing a tab in double quotes.`
            : `Line ${lineNumber}: it looks like the text or value contains a comma. Wrap that part in double quotes so it isn't split — for example ${QUOTE_EXAMPLE}.`,
      });
      return;
    }

    if (fields.length === 2 && (fields[1] ?? '') === '') {
      // A trailing separator with nothing after it (`Text,`) is treated as a
      // mistake rather than an intentional empty value — an empty option value
      // is almost never wanted, and this steers users to enter just the text
      // (no separator) when they want the text reused as the value.
      errors.push({
        line: lineNumber,
        message: `Line ${lineNumber}: add a value after the ${separator}, or remove the ${separator} to reuse the text as the value.`,
      });
      return;
    }

    // With no separator the text is reused as the value; otherwise the value is
    // the second field (which, when quoted, may contain commas).
    const value = fields.length === 2 ? (fields[1] ?? '') : text;
    items.push({text, value});
  });

  if (errors.length === 0 && items.length === 0) {
    errors.push({
      line: 0,
      message: 'Enter at least one option, with one option per line.',
    });
  }

  if (errors.length > 0) {
    return {ok: false, errors};
  }

  return {ok: true, items};
};
